"use client";

import React from "react";
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface FormErrorProps extends React.HTMLAttributes<HTMLDivElement> {
  message?: string | null;
}

export function FormError({ message, className, ...props }: FormErrorProps) {
  return <FormAlert message={message} type="error" className={className} {...props} />;
}

interface FormSuccessProps extends React.HTMLAttributes<HTMLDivElement> {
  message?: string | null;
}

export function FormSuccess({
  message,
  className,
  ...props
}: FormSuccessProps) {
  return <FormAlert message={message} type="success" className={className} {...props} />;
}

interface FormAlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  message?: React.ReactNode;
  title?: React.ReactNode;
  type?: "error" | "success" | "info" | "warning";
}

export function FormAlert({
  message,
  title,
  type = "info",
  className,
  ...props
}: FormAlertProps) {
  if (!message) return null;

  const config = {
    error: { icon: AlertCircle, variant: "destructive" as const, title: "Something went wrong" },
    success: { icon: CheckCircle2, variant: "success" as const, title: "Success" },
    info: { icon: Info, variant: "default" as const, title: "Information" },
    warning: { icon: TriangleAlert, variant: "warning" as const, title: "Please check this" }
  }[type];
  const Icon = config.icon;

  return (
    <Alert
      variant={config.variant}
      className={className}
      role={type === "error" ? "alert" : "status"}
      aria-live={type === "error" ? "assertive" : "polite"}
      {...props}
    >
      <Icon />
      <AlertTitle>{title ?? config.title}</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}
